import { useContext, useState } from "react"
import { Dropdown } from "primereact/dropdown"
import { classNames } from "primereact/utils"
import { Calendar } from "primereact/calendar"
import { DateTime } from "luxon"
import { Button } from "primereact/button"
import { Badge } from "primereact/badge"

// Custom components
import CustomTable from "../CustomTable/CustomTable"
import { UserContext } from "../../context/userContext"

// Utils
import { formatOrgCalDate, getDateTimeValue, hasPendingChange, pendingChangeTooltip, requestModalModeOptions, rescheduleCancellationTooltip } from "./utils"

// Styles
import myStyles from "../../styles/appointments/Appointments.module.css"
import { Tooltip } from "primereact/tooltip"

export default function AppointmentsListView({
  filterDateRange,
  handleDateRangeChanged,
  filteredAppointments,
  openEditModal,
  openDeleteConfirmation,
  openCancelConfirmation,
  loadingAppointmentsSmall,
  getOrgNameFromMap,
}) {
  const userCtx = useContext(UserContext)
  const [activeStaffSchedulingIndex, setActiveStaffSchedulingIndex] = useState(0) // State to control which tab is active in the list view (e.g., Upcoming, Past, Pending).

  function RenderTooltipIcon(target, tooltipText) {
    return (
      <div className={myStyles.infoWithTooltipList}>
        <Tooltip target={target} />
        <i
          id={target.replace("#", "")}
          className="pi pi-info-circle"
          style={{ fontSize: '1.2rem' }}
          data-pr-tooltip={tooltipText}
          data-pr-position="left"
        />
      </div>
    )
  }

  // Formats the actions column for pending appointment requests in the list view.
  const formatApptActions = (rowData) => {
    return (
      <div className="flex flex-row flex-wrap gap-2">
        {(rowData.type === "RESCHEDULE" || rowData.type === "CANCELLING") && (
          RenderTooltipIcon(`#change_button_info_pending_${rowData.id}`, rescheduleCancellationTooltip.replace("[TYPE]", rowData.type))
        )}
        {(rowData.type === null) && (
          RenderTooltipIcon(`#change_button_info_pending_${rowData.id}`, pendingChangeTooltip)
        )}
        <Button
          severity="primary"
          className={myStyles.roundedButton}
          onClick={() => {
            openEditModal(rowData, requestModalModeOptions.edit) // Open edit modal with "edit" mode.
          }}
          disabled={rowData.type !== "NEW" || loadingAppointmentsSmall} // Disable edit if not a "NEW" type request.
          icon="pi pi-pencil"
          tooltip="Edit"
          tooltipOptions={{ position: "bottom" }}
        />
        <Button
          severity="secondary"
          className={myStyles.roundedButton}
          onClick={() => openDeleteConfirmation(rowData)} // Call confirmDelete on click.
          icon="pi pi-trash"
          tooltip="Delete"
          tooltipOptions={{ position: "bottom" }}
          disabled={loadingAppointmentsSmall} // Disable if loading.
        />
      </div>
    )
  }

  // Gets the formatted date range string for a recurring appointment controller.
  const getRecurringControllerRange = (appt) => {
    const startDate = getDateTimeValue(appt.startDate) // Get Luxon DateTime for start date.
    const endDate = getDateTimeValue(appt.endDate) // Get Luxon DateTime for end date.
    return (
      startDate.toFormat("MM/dd/yyyy") +
      " - " +
      endDate.toFormat("MM/dd/yyyy")
    )
  }

  // Formats the actions column for upcoming appointment requests in the list view.
  function formatRequestActions(rowData) {
    const _hasPendingChange = hasPendingChange(rowData)

    return (
      <div className="flex flex-row flex-wrap gap-2">
        {_hasPendingChange && (
          RenderTooltipIcon(`#change_button_info_${rowData.id}`, pendingChangeTooltip)
        )}
        <Button
          className={myStyles.roundedButton}
          onClick={() => {
            openEditModal(rowData, requestModalModeOptions.change) // Open edit modal in "requesting change" mode.
          }}
          disabled={_hasPendingChange || loadingAppointmentsSmall}
          icon="pi pi-pencil"
          tooltip="Change"
          tooltipOptions={{ position: "bottom" }}
        />
        <Button
          severity="secondary"
          className={myStyles.roundedButton}
          disabled={_hasPendingChange || loadingAppointmentsSmall}
          onClick={() => openCancelConfirmation(rowData)} // Open cancel confirmation dialog.
          icon="pi pi-trash"
          tooltip="Cancel"
          tooltipOptions={{ position: "bottom" }}
        />
      </div>
    )
  }

  function formatApptTimeCol(rowData) {
    return rowData.isRecurring ?
      `${formatOrgCalDate(rowData, "recurringStartDate")} - ${formatOrgCalDate(rowData, "recurringEndDate")}` :
      formatOrgCalDate(rowData, "startDateTime")
  }

  function formatApptTypeCol(rowData) {
    return (
      <span>
        {rowData.engagementType}
        {rowData.isRecurring && (
          <div>
            <Badge
              value="Recurring"
              className="mt-1"
            />
          </div>
        )}
      </span>
    )
  }

  // Renders the action button for editing recurring appointment controllers in the list view.
  function EditRecurringAppointmentsColumnBody(rowData) {
    const onClick = () => {
      // Find the corresponding controller from the cached list.
      const controller = filteredAppointments.recurring.filter(
        (x) => x.id.toString() === rowData.id.toString()
      )
      if (controller.length > 0) {
        openEditModal(controller[0], requestModalModeOptions.change) // Open edit modal in "change" mode.
      } else {
        userCtx.setNotificationError(
          "Unable to find the recurring appointment controller for this instance"
        )
      }
    }

    const _hasPendingChange = hasPendingChange(rowData)

    return (
      <div className="flex flex-row flex-wrap gap-2">
        {(_hasPendingChange) && (
          RenderTooltipIcon(`#change_button_info_${rowData.id}`, pendingChangeTooltip)
        )}
        <Button
          className={myStyles.roundedButton}
          onClick={onClick}
          disabled={_hasPendingChange || loadingAppointmentsSmall}
          icon="pi pi-pencil"
          tooltip="Change"
          tooltipOptions={{ position: "bottom" }}
        />
        <Button
          severity="secondary"
          className={myStyles.roundedButton}
          disabled={_hasPendingChange || loadingAppointmentsSmall}
          icon="pi pi-trash"
          tooltip="Cancel"
          tooltipOptions={{ position: "bottom" }}
          onClick={() => openCancelConfirmation(rowData)} // Open cancel confirmation dialog.
        />
      </div>
    )
  }

  function capitalizeFirstLetter(str) {
    if (typeof str !== 'string' || !str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const listViewSelectItemTemplate = (title, count) => {
    return (
      <span className={myStyles.semiboldText}>
        {title}
        {count > 0 && <Badge className="ml-2" value={count} />}
      </span>
    )
  }

  const pendingApptsHeaders = () => {
    return [
      { value: "startDateTime", label: "Date", size: 2 },
      { value: "scheduledTime", label: "Time", size: 2 },
      { value: "engagementType", label: "Engagement", size: 2 },
      { value: "engagementChannel", label: "Channel", size: 1 },
      { value: "staff", label: "Staff", size: 2 },
      { value: "type", label: "Request", size: 1 },
      { value: "action", label: "", size: 2, isActionColumn: true },
    ]
  }

  const pendingApptsCollection = () => {
    return filteredAppointments.pending.map(appt => ({
      startDateTime: formatApptTimeCol(appt),
      scheduledTime: appt.scheduledTime,
      engagementType: formatApptTypeCol(appt),
      engagementChannel: capitalizeFirstLetter(appt.engagementChannel),
      staff: `${appt.staff} (${getOrgNameFromMap(appt.orgId)})`,
      type: `${appt.type !== null ? appt.type : "NEW (EDITED)"}`,
      action: formatApptActions(appt),
    }))
  }

  const pendingApptsSorting = () => {
    return [
      {
        value: "sortBy",
        label: "",
        options: [
          { name: "Date", code: "startDateTime" },
          { name: "Engagement Type", code: "engagementType" },
          { name: "Engagement Channel", code: "engagementChannel" },
          { name: "Scheduled Time", code: "scheduledTime" },
          { name: "Staff", code: "staff" },
          { name: "Type", code: "type" },
        ],
        default: "startDateTime",
        defaultOrder: true,
        showClear: true,
        type: "multiselect",
      }
    ]
  }

  const declinedApptsHeaders = () => {
    return [
      { value: "startDateTime", label: "Date", size: 2 },
      { value: "scheduledTime", label: "Time", size: 2 },
      { value: "engagementType", label: "Engagement", size: 2 },
      { value: "engagementChannel", label: "Channel", size: 2 },
      { value: "staff", label: "Staff", size: 2 },
    ]
  }

  const declinedApptsCollection = () => {
    return filteredAppointments.declined.map(appt => ({
      startDateTime: formatOrgCalDate(appt, "startDateTime"),
      scheduledTime: appt.scheduledTime,
      engagementType: formatApptTypeCol(appt),
      engagementChannel: appt.engagementChannel,
      staff: `${appt.staff} (${getOrgNameFromMap(appt.orgId)})`,
    }))
  }

  const declinedApptsSorting = () => {
    return [{
      value: "sortBy",
      label: "",
      options: [
        { name: "Date", code: "startDateTime" },
        { name: "Engagement Type", code: "engagementType" },
        { name: "Engagement Channel", code: "engagementChannel" },
        { name: "Scheduled Time", code: "scheduledTime" },
        { name: "Staff", code: "staff" },
      ],
      default: "startDateTime",
      defaultOrder: true,
      showClear: true,
      type: "multiselect",
    }]
  }

  const pastApptsHeaders = () => {
    return [
      { value: "startDateTime", label: "Date", size: 2 },
      { value: "scheduledTime", label: "Time", size: 2 },
      { value: "engagementType", label: "Engagement", size: 2 },
      { value: "engagementChannel", label: "Channel", size: 2 },
      { value: "staff", label: "Staff", size: 2 },
    ]
  }

  const pastApptsCollection = () => {
    return filteredAppointments.past.map(appt => ({
      startDateTime: formatOrgCalDate(appt, "startDateTime"),
      engagementType: appt.engagementType,
      engagementChannel: appt.engagementChannel,
      scheduledTime: appt.scheduledTime,
      staff: `${appt.staff} (${getOrgNameFromMap(appt.orgId)})`,
    }))
  }

  const pastApptsSorting = () => {
    return [{
      value: "sortBy",
      label: "",
      options: [
        { name: "Date", code: "startDateTime" },
        { name: "Engagement Type", code: "engagementType" },
        { name: "Engagement Channel", code: "engagementChannel" },
        { name: "Scheduled Time", code: "scheduledTime" },
        { name: "Staff", code: "staff" },
      ],
      default: "startDateTime",
      defaultOrder: true,
      showClear: true,
      type: "multiselect",
    }]
  }

  const upcomingApptsHeaders = () => {
    return [
      { value: "startDateTime", label: "Date", size: 2 },
      { value: "scheduledTime", label: "Time", size: 2 },
      { value: "engagementType", label: "Engagement", size: 2 },
      { value: "engagementChannel", label: "Channel", size: 2 },
      { value: "staff", label: "Staff", size: 2 },
      { value: "action", label: "", size: 2, isActionColumn: true },
    ]
  }

  const upcomingApptsCollection = () => {
    return filteredAppointments.upcoming.map(appt => ({
      startDateTime: formatOrgCalDate(appt, "startDateTime"),
      scheduledTime: appt.scheduledTime,
      engagementType: appt.engagementType,
      engagementChannel: appt.engagementChannel,
      staff: `${appt.staff} (${getOrgNameFromMap(appt.orgId)})`,
      action: formatRequestActions(appt),
    }))
  }

  const upcomingApptsSorting = () => {
    return [{
      value: "sortBy",
      label: "",
      options: [
        { name: "Date", code: "startDateTime" },
        { name: "Engagement Type", code: "engagementType" },
        { name: "Engagement Channel", code: "engagementChannel" },
        { name: "Scheduled Time", code: "scheduledTime" },
        { name: "Staff", code: "staff" },
      ],
      default: "startDateTime",
      defaultOrder: true,
      showClear: true,
      type: "multiselect",
    }]
  }

  const recurringApptsHeaders = () => {
    return [
      { value: "description", label: "Description", size: 3 },
      { value: "range", label: "Date Range", size: 3 },
      { value: "engagementChannel", label: "Channel", size: 2 },
      { value: "staff", label: "Staff", size: 2 },
      { value: "action", label: "", size: 2, isActionColumn: true },
    ]
  }

  const recurringApptsCollection = () => {
    return filteredAppointments.recurring.map(appt => ({
      description: appt.localFrequency,
      range: getRecurringControllerRange(appt),
      engagementChannel: appt.engagementChannel,
      staff: `${appt.staff} (${getOrgNameFromMap(appt.orgId)})`,
      action: EditRecurringAppointmentsColumnBody(appt),
    }))
  }

  const recurringApptsSorting = () => {
    return [{
      value: "sortBy",
      label: "",
      options: [
        { name: "Date", code: "newFreq" },
        { name: "Engagement Type", code: "engagementType" },
        { name: "Engagement Channel", code: "engagementChannel" },
        { name: "Range", code: "range" },
        { name: "Staff", code: "staff" },
      ],
      default: "newFreq",
      defaultOrder: true,
      showClear: true,
      type: "multiselect",
    }]
  }

  return (
    <>
      <hr className={myStyles.listViewTopHR} />

      <div className={myStyles.listViewHeader}>
        <Dropdown
          value={activeStaffSchedulingIndex} // Currently active tab/list.
          onChange={(e) => setActiveStaffSchedulingIndex(e.value)} // Change active tab.
          options={[ // Options for the list view dropdown.
            { label: "Upcoming Appointments", value: 0, code: "upcoming" },
            { label: "Past Appointments", value: 1, code: "past" },
            { label: "Pending Requests", value: 2, code: "pending" },
            { label: "Declined Requests", value: 3, code: "declined" },
            { label: "Recurring Controllers", value: 4, code: "recurring" },
          ]}
          optionLabel="label"
          className={classNames(myStyles.listViewSelect)}
          itemTemplate={(option) => {
            return listViewSelectItemTemplate(option.label, filteredAppointments[option.code]?.length || 0)
          }}
        />

        <Calendar
          id="range"
          className={classNames(myStyles.listDateFilter, myStyles.filterItemBorder)}
          value={filterDateRange[0]} // Current date range for filtering.
          onChange={(e) => {
            const _filterDateStart = DateTime.fromJSDate(e.value).startOf('month').toJSDate()
            const _filterDateEnd = DateTime.fromJSDate(e.value).endOf('month').toJSDate()
            handleDateRangeChanged([_filterDateStart, _filterDateEnd])
          }}
          // selectionMode="range" // Enable range selection.
          view="month"
          dateFormat="MM yy"
        />
      </div>

      {/* Render CustomTable based on activeStaffSchedulingIndex */}
      {activeStaffSchedulingIndex === 0 && (
        <div className={filteredAppointments.upcoming?.length > 0 ? myStyles.listViewContent : myStyles.listViewContentEmpty}>
          <CustomTable
            headers={upcomingApptsHeaders()}
            collection={upcomingApptsCollection()}
            filters={[]} // No additional filters applied here, as handled by filterAppointments.
            sorting={upcomingApptsSorting()}
            disablePagination // Pagination disabled for simplicity.
            singleSortOnly
          />
        </div>
      )}

      {activeStaffSchedulingIndex === 1 && (
        <div className={filteredAppointments.past?.length > 0 ? myStyles.listViewContent : myStyles.listViewContentEmpty}>
          <CustomTable
            headers={pastApptsHeaders()}
            collection={pastApptsCollection()}
            filters={[]}
            sorting={pastApptsSorting()}
            disablePagination
            singleSortOnly
          />
        </div>
      )}

      {activeStaffSchedulingIndex === 2 && (
        <div className={filteredAppointments.pending?.length > 0 ? myStyles.listViewContent : myStyles.listViewContentEmpty}>
          <CustomTable
            headers={pendingApptsHeaders()}
            collection={pendingApptsCollection()}
            filters={[]}
            sorting={pendingApptsSorting()}
            disablePagination
            singleSortOnly
          />
        </div>
      )}

      {activeStaffSchedulingIndex === 3 && (
        <div className={filteredAppointments.declined?.length > 0 ? myStyles.listViewContent : myStyles.listViewContentEmpty}>
          <CustomTable
            headers={declinedApptsHeaders()}
            collection={declinedApptsCollection()}
            filters={[]}
            sorting={declinedApptsSorting()}
            disablePagination
            singleSortOnly
          />
        </div>
      )}

      {activeStaffSchedulingIndex === 4 && (
        <div className={filteredAppointments.recurring?.length > 0 ? myStyles.listViewContent : myStyles.listViewContentEmpty}>
          <CustomTable
            headers={recurringApptsHeaders()}
            collection={recurringApptsCollection()}
            filters={[]}
            sorting={recurringApptsSorting()}
            disablePagination
            singleSortOnly
          />
        </div>
      )}
    </>
  )
}
